import {
  isSnapshotDeployable,
  isSnapshotRoot,
  snapshotRootRef,
  type SnapshotWithRoot,
} from "../../ctrl/hooks/snapshotFilters";
import {
  getSnapshotDisplayName,
  getSnapshotRelativeTime,
  getSnapshotShortSha,
} from "../../ctrl/snapshots/helpers";
import type { StatusTone } from "@repo/ui/na";

export type SnapStatus = "success" | "failed" | "canceled" | "unknown";
export type SnapKind = "aaos" | "yocto" | "sync";

export const KIND_LABEL: Record<SnapKind, string> = {
  aaos: "AAOS image",
  yocto: "Yocto Linux image",
  sync: "Source sync",
};
export const KIND_SHORT: Record<SnapKind, string> = { aaos: "AAOS", yocto: "Yocto", sync: "Sync" };

export const SNAP_TONE: Record<SnapStatus, StatusTone> = {
  success: "success",
  failed: "danger",
  canceled: "warning",
  unknown: "muted",
};
export const SNAP_STATUS_LABEL: Record<SnapStatus, string> = {
  success: "Success",
  failed: "Failed",
  canceled: "Canceled",
  unknown: "Unknown",
};

export const GIB = 1024 ** 3;

export function snapStatus(s: SnapshotWithRoot): SnapStatus {
  const v = s.labels?.["null.autos/job-status"];
  return v === "success" || v === "failed" || v === "canceled" ? v : "unknown";
}

/**
 * Snapshot type from the `null.autos/image-type` label, falling back to
 * job-name heuristics on ctrl builds that don't stamp it.
 */
export function snapKind(s: SnapshotWithRoot): SnapKind {
  const t = s.labels?.["null.autos/image-type"];
  if (t === "cuttlefish") return "aaos";
  if (t === "wic") return "yocto";
  if (t === "") return "sync";
  const job = `${s.job_name ?? ""} ${s.name ?? ""}`.toLowerCase();
  if (/yocto|bitbake|wic/.test(job)) return "yocto";
  if (/sync/.test(job)) return "sync";
  return "aaos";
}

export const isPhantom = (s: SnapshotWithRoot) => s.phantom === true;
export const isBad = (s: SnapshotWithRoot) =>
  isPhantom(s) || snapStatus(s) === "failed" || snapStatus(s) === "canceled";

export function fmtSize(bytes?: number) {
  const g = (bytes ?? 0) / GIB;
  if (g === 0) return "0 B";
  if (g < 1) return `${Math.round(g * 1024)} MiB`;
  return `${g.toFixed(1)} GiB`;
}

export function rootLabel(s: SnapshotWithRoot) {
  return `Root · ${snapshotRootRef(s) ?? s.job_ref ?? "root"}`;
}

/** Why an AAOS snapshot can't deploy, in one line. */
export function notDeployableReason(s: SnapshotWithRoot): string | undefined {
  if (snapKind(s) !== "aaos" || isSnapshotDeployable(s)) return undefined;
  if (isPhantom(s)) return "Data plane lost; never usable";
  const st = snapStatus(s);
  if (st === "failed") return `CI job failed${s.job_stage ? ` at stage ${s.job_stage}` : ""}`;
  if (st === "canceled") return "CI job canceled";
  if (!s.ready) return "Not ready";
  if (!s.labels?.arch) return "Missing vehicle and arch labels";
  return "Not deployable";
}

export type SnapView = {
  s: SnapshotWithRoot;
  name: string;
  label: string;
  sha?: string;
  ago?: string;
  status: SnapStatus;
  tone: StatusTone;
  kind: SnapKind;
  root: boolean;
  deployable: boolean;
  phantom: boolean;
  parent?: string;
  created: number;
};

export function snapView(s: SnapshotWithRoot): SnapView {
  const status = snapStatus(s);
  return {
    s,
    name: s.name ?? "",
    label: getSnapshotDisplayName(s),
    sha: getSnapshotShortSha(s) ?? s.commit_sha_short,
    ago: getSnapshotRelativeTime(s),
    status,
    tone: SNAP_TONE[status],
    kind: snapKind(s),
    root: isSnapshotRoot(s),
    deployable: isSnapshotDeployable(s),
    phantom: isPhantom(s),
    parent: s.source_snapshot || undefined,
    created: Date.parse(s.created_at ?? "") || 0,
  };
}

/**
 * Who a snapshot is, with label fallbacks: root and manually-made
 * snapshots often carry their metadata only in labels, not the CI fields.
 */
export function snapIdentity(s: SnapshotWithRoot) {
  const labels = s.labels ?? {};
  const project = s.project?.trim() || labels.proj || labels.vehicle || undefined;
  const ref = s.job_ref?.trim() || s.commit_ref?.trim() || undefined;
  const sha = s.commit_sha_short || s.job_sha?.slice(0, 7) || undefined;
  const root = isSnapshotRoot(s);
  const rootRef = snapshotRootRef(s) ?? labels["null.autos/root-ref"];
  // The build line a newer snapshot must share to be an "update": a root
  // group for roots, project + ref for CI builds. Unknown → no updates.
  const line = root
    ? rootRef
      ? `root:${rootRef}`
      : undefined
    : project && ref
      ? `build:${project}|${ref}`
      : undefined;
  const lineLabel = root ? (rootRef ? `root ${rootRef}` : undefined) : project && ref ? `${project.split("/").pop()} · ${ref}` : undefined;
  return {
    project,
    projectLeaf: project?.split("/").pop(),
    ref,
    sha,
    root,
    rootRef,
    line,
    lineLabel,
    /** Short handle for copy: SHA, else the snapshot name. */
    handle: sha ?? s.name ?? "snapshot",
  };
}

/** Deployable snapshots newer than `snap` on the same build line, newest first. */
export function newerOnLine(snap: SnapshotWithRoot, all: SnapshotWithRoot[]): SnapshotWithRoot[] {
  const { line } = snapIdentity(snap);
  if (!line) return [];
  const t = Date.parse(snap.created_at ?? "") || 0;
  return all
    .filter(
      (s) =>
        s.name !== snap.name &&
        isSnapshotDeployable(s) &&
        (Date.parse(s.created_at ?? "") || 0) > t &&
        snapIdentity(s).line === line,
    )
    .sort((a, b) => Date.parse(b.created_at ?? "") - Date.parse(a.created_at ?? ""));
}
